import React, { useState, useEffect, useMemo, useCallback } from 'react';

const PRIMARY = '#0052CC';

export default function ONUTypesPage() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editType, setEditType] = useState(null);
  const [form, setForm] = useState({ model_name: '', manufacturer: 'Huawei', eth_ports: 4, tel_ports: 1, usb_ports: 1, has_wifi: true, has_catv: false, default_line_profile: '', default_service_profile: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const api = useMemo(() => {
    const token = localStorage.getItem('session_token');
    const headers = { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) };
    return {
      get: (url) => fetch(url, { headers }).then(r => r.json()),
      post: (url, body) => fetch(url, { method: 'POST', headers, body: JSON.stringify(body) }).then(r => r.json()),
      put: (url, body) => fetch(url, { method: 'PUT', headers, body: JSON.stringify(body) }).then(r => r.json()),
      del: (url) => fetch(url, { method: 'DELETE', headers }).then(r => r.json()),
    };
  }, []);

  const loadTypes = useCallback(async () => {
    setLoading(true);
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const data = await api.get(`/api/noc/onu-types${params}`);
      setTypes(data.items || []);
    } catch (e) { setError('Failed to load ONU types'); }
    finally { setLoading(false); }
  }, [api, search]);

  useEffect(() => { loadTypes(); }, [loadTypes]);

  const openAdd = () => {
    setEditType(null);
    setForm({ model_name: '', manufacturer: 'Huawei', eth_ports: 4, tel_ports: 1, usb_ports: 1, has_wifi: true, has_catv: false, default_line_profile: '', default_service_profile: '', description: '' });
    setShowForm(true);
  };
  const openEdit = (t) => {
    setEditType(t);
    setForm({ model_name: t.model_name, manufacturer: t.manufacturer || 'Huawei', eth_ports: t.eth_ports || 4, tel_ports: t.tel_ports || 1, usb_ports: t.usb_ports || 1, has_wifi: !!t.has_wifi, has_catv: !!t.has_catv, default_line_profile: t.default_line_profile || '', default_service_profile: t.default_service_profile || '', description: t.description || '' });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.model_name.trim()) { setError('Model name is required'); return; }
    setSaving(true); setError('');
    try {
      if (editType) { await api.put(`/api/noc/onu-types/${editType.id}`, form); }
      else { await api.post('/api/noc/onu-types', form); }
      setShowForm(false); loadTypes();
    } catch (e) { setError('Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this ONU type?')) return;
    try { await api.del(`/api/noc/onu-types/${id}`); loadTypes(); }
    catch (e) { setError('Delete failed'); }
  };

  const Badge = ({ ok }) => (
    <span style={{ background: ok ? '#E3FCEF' : '#F4F5F7', color: ok ? '#006644' : '#6B778C', borderRadius: 4, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>
      {ok ? '✓' : '—'}
    </span>
  );

  return (
    <div style={{ padding: '24px', background: '#f5f7fa', minHeight: '100vh' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#172B4D' }}>ONU Types</h2>
          <p style={{ margin: '4px 0 0', color: '#6B778C', fontSize: 13 }}>Manage ONU/ONT device models and their default profiles</p>
        </div>
        <button onClick={openAdd} style={{ background: PRIMARY, color: '#fff', border: 'none', borderRadius: 6, padding: '9px 18px', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
          + Add ONU Type
        </button>
      </div>

      {error && <div style={{ background: '#FFEBE6', border: '1px solid #FF5630', borderRadius: 6, padding: '10px 14px', marginBottom: 16, color: '#BF2600', fontSize: 13 }}>{error}</div>}

      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #DFE1E6', padding: '14px 16px', marginBottom: 16, display: 'flex', gap: 10 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search model name or manufacturer..." onKeyDown={e => e.key === 'Enter' && loadTypes()}
          style={{ flex: 1, border: '1px solid #DFE1E6', borderRadius: 5, padding: '7px 12px', fontSize: 14, outline: 'none' }} />
        <button onClick={loadTypes} style={{ background: PRIMARY, color: '#fff', border: 'none', borderRadius: 5, padding: '7px 16px', cursor: 'pointer', fontSize: 14 }}>Search</button>
      </div>

      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #DFE1E6', overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#F4F5F7' }}>
              {['#', 'Model', 'Manufacturer', 'ETH', 'TEL', 'USB', 'WiFi', 'CATV', 'Line Profile', 'Service Profile', 'Actions'].map(h => (
                <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontWeight: 600, color: '#5E6C84', borderBottom: '1px solid #DFE1E6', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} style={{ padding: 32, textAlign: 'center', color: '#6B778C' }}>Loading...</td></tr>
            ) : types.length === 0 ? (
              <tr><td colSpan={11} style={{ padding: 32, textAlign: 'center', color: '#6B778C' }}>No ONU types found.</td></tr>
            ) : types.map((t, i) => (
              <tr key={t.id} style={{ borderBottom: '1px solid #F4F5F7' }} onMouseEnter={e => e.currentTarget.style.background='#F8F9FA'} onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                <td style={{ padding: '10px 14px', color: '#6B778C' }}>{i + 1}</td>
                <td style={{ padding: '10px 14px', fontWeight: 700, color: '#172B4D' }}>{t.model_name}</td>
                <td style={{ padding: '10px 14px', color: '#344563' }}>{t.manufacturer}</td>
                <td style={{ padding: '10px 14px', textAlign: 'center' }}>{t.eth_ports}</td>
                <td style={{ padding: '10px 14px', textAlign: 'center' }}>{t.tel_ports}</td>
                <td style={{ padding: '10px 14px', textAlign: 'center' }}>{t.usb_ports}</td>
                <td style={{ padding: '10px 14px', textAlign: 'center' }}><Badge ok={t.has_wifi} /></td>
                <td style={{ padding: '10px 14px', textAlign: 'center' }}><Badge ok={t.has_catv} /></td>
                <td style={{ padding: '10px 14px', color: '#6B778C', fontSize: 12 }}>{t.default_line_profile || '—'}</td>
                <td style={{ padding: '10px 14px', color: '#6B778C', fontSize: 12 }}>{t.default_service_profile || '—'}</td>
                <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                  <button onClick={() => openEdit(t)} style={{ background: '#E3FCEF', color: '#006644', border: 'none', borderRadius: 4, padding: '5px 12px', cursor: 'pointer', marginRight: 6, fontSize: 12 }}>Edit</button>
                  <button onClick={() => handleDelete(t.id)} style={{ background: '#FFEBE6', color: '#BF2600', border: 'none', borderRadius: 4, padding: '5px 12px', cursor: 'pointer', fontSize: 12 }}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: 28, width: 560, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: '#172B4D' }}>{editType ? 'Edit ONU Type' : 'Add ONU Type'}</h3>
            {error && <div style={{ background: '#FFEBE6', border: '1px solid #FF5630', borderRadius: 5, padding: '8px 12px', marginBottom: 14, color: '#BF2600', fontSize: 13 }}>{error}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label style={{ display: 'block', fontWeight: 600, color: '#344563', fontSize: 13, marginBottom: 5 }}>Model Name *</label>
                <input value={form.model_name} onChange={e => setForm(p => ({ ...p, model_name: e.target.value }))} placeholder="e.g. EG8141A5"
                  style={{ width: '100%', border: '1px solid #DFE1E6', borderRadius: 5, padding: '8px 12px', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, color: '#344563', fontSize: 13, marginBottom: 5 }}>Manufacturer</label>
                <input value={form.manufacturer} onChange={e => setForm(p => ({ ...p, manufacturer: e.target.value }))} placeholder="e.g. Huawei"
                  style={{ width: '100%', border: '1px solid #DFE1E6', borderRadius: 5, padding: '8px 12px', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              {[['eth_ports','ETH Ports'],['tel_ports','TEL Ports'],['usb_ports','USB Ports']].map(([k,l]) => (
                <div key={k}>
                  <label style={{ display: 'block', fontWeight: 600, color: '#344563', fontSize: 13, marginBottom: 5 }}>{l}</label>
                  <input type="number" value={form[k]} onChange={e => setForm(p => ({ ...p, [k]: parseInt(e.target.value)||0 }))} min={0}
                    style={{ width: '100%', border: '1px solid #DFE1E6', borderRadius: 5, padding: '8px 12px', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                </div>
              ))}
              <div style={{ display: 'flex', gap: 20, alignItems: 'center', paddingTop: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600, color: '#344563', fontSize: 13 }}>
                  <input type="checkbox" checked={form.has_wifi} onChange={e => setForm(p => ({ ...p, has_wifi: e.target.checked }))} style={{ width: 16, height: 16 }} />
                  WiFi
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600, color: '#344563', fontSize: 13 }}>
                  <input type="checkbox" checked={form.has_catv} onChange={e => setForm(p => ({ ...p, has_catv: e.target.checked }))} style={{ width: 16, height: 16 }} />
                  CATV
                </label>
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, color: '#344563', fontSize: 13, marginBottom: 5 }}>Default Line Profile</label>
                <input value={form.default_line_profile} onChange={e => setForm(p => ({ ...p, default_line_profile: e.target.value }))} placeholder="e.g. line-profile-1"
                  style={{ width: '100%', border: '1px solid #DFE1E6', borderRadius: 5, padding: '8px 12px', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, color: '#344563', fontSize: 13, marginBottom: 5 }}>Default Service Profile</label>
                <input value={form.default_service_profile} onChange={e => setForm(p => ({ ...p, default_service_profile: e.target.value }))} placeholder="e.g. service-profile-1"
                  style={{ width: '100%', border: '1px solid #DFE1E6', borderRadius: 5, padding: '8px 12px', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={{ display: 'block', fontWeight: 600, color: '#344563', fontSize: 13, marginBottom: 5 }}>Description</label>
                <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={2} placeholder="Optional notes..."
                  style={{ width: '100%', border: '1px solid #DFE1E6', borderRadius: 5, padding: '8px 12px', fontSize: 14, outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
              <button onClick={() => setShowForm(false)} style={{ background: '#F4F5F7', color: '#344563', border: 'none', borderRadius: 5, padding: '9px 20px', cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ background: PRIMARY, color: '#fff', border: 'none', borderRadius: 5, padding: '9px 20px', cursor: 'pointer', fontWeight: 600 }}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
